import { useEffect, useMemo, useState } from 'react';
import { useParams, useLocation, NavLink, Link, Outlet } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button } from '@mantine/core';
import { EMPTY_PERSON, type Business } from '@turizi/shared';
import { setWebTitle } from '@/lib/format-utils';
import { usePerson, useUpdatePerson } from '@/hooks/api-hooks';
import { SkeletonListWithFilters } from '@/components/snippets';
import PersonHeader from '@/components/person-page-header';
import CardModal from '@/components/card-modal';
import CreateBusinessComponent from '@/components/create-new-business-component';
import { BUSINESSES_PATH } from '@/lib/navigation';
import {
    IconInfoCircle,
    IconBus,
    IconBuildingStore,
    IconArrowUpRight,
    IconArrowsExchange,
    IconLink,
    IconLinkOff,
} from '@tabler/icons-react';


export default function EditPerson(): JSX.Element {
    const { t } = useTranslation();
    const { id } = useParams<{ id: string }>();
    const { pathname } = useLocation();
    const activeSection = pathname.split('/').pop() || 'details';

    const {
        data,
        isLoading,
    } = usePerson(id);
    const person = data?.data || EMPTY_PERSON;

    const updatePersonMutation = useUpdatePerson();
    const [isBusinessModalOpen, setIsBusinessModalOpen] = useState(false);

    const linkedBusiness = person?.business;
    const linkedBusinessId = person?.business_id || linkedBusiness?.id;
    const linkedBusinessName = linkedBusiness?.name;

    const handleLinkBusiness = (selectedBusiness: Business) => {
        if (!person?.id || !selectedBusiness?.id) return;
        updatePersonMutation.mutate(
            { id: person.id, business_id: selectedBusiness.id },
            {
                onSuccess: () => {
                    setIsBusinessModalOpen(false);
                },
            }
        );
    };

    const handleUnlinkBusiness = () => {
        if (!person?.id) return;
        if (!window.confirm(t('confirm_unlink_business'))) return;
        updatePersonMutation.mutate({ id: person.id, business_id: null });
    };

    const sectionItems = useMemo(() => [
        { key: 'details', label: t('details'), icon: IconInfoCircle },
        { key: 'reservations_as_pax', label: t('reservations_as_pax'), icon: IconBus },
    ], [t]);

    setWebTitle(`${person?.name || ''} · ${t(activeSection)}`);

    // Reset title when component unmounts
    useEffect(() => () => {
        setWebTitle(t('persons'));
    }, [t]);

    if (isLoading || !person?.id) {
        return (
            <SkeletonListWithFilters />
        );
    }

    return (
        <div className="space-y-4">
            {/* Top Header Card */}
            {person && (
                <PersonHeader
                    person={person}
                    isLoading={false}
                />
            )}

            {/* Sidebar + Main Content Column */}
            <div className="flex flex-col md:flex-row gap-4 md:gap-6">
                {/* Left Side Menu (Sidebar) */}
                <aside className="w-full md:w-[220px] shrink-0">
                    <div className="sticky top-20 space-y-3">
                        <div className="flex flex-row md:flex-col gap-1 overflow-x-auto md:overflow-x-visible pb-2 md:pb-0 border-b border-gray-200 md:border-b-0">
                            {sectionItems.map((section) => {
                                const Icon = section.icon;
                                return (
                                    <NavLink
                                        key={section.key}
                                        to={section.key}
                                        className={({ isActive }) =>
                                            `flex items-center gap-2.5 px-3 py-2 rounded-lg text-xs font-medium transition-all duration-150 ${isActive
                                                ? 'bg-gray-100 text-gray-900 shadow-xs'
                                                : 'text-gray-500 hover:text-gray-900 hover:bg-gray-50/80'
                                            }`
                                        }
                                        end
                                    >
                                        {({ isActive }) => (
                                            <>
                                                <Icon size={16} className={
                                                    isActive
                                                        ? 'text-gray-900 shrink-0'
                                                        : 'text-gray-400 shrink-0'} stroke={2}
                                                />
                                                <span className={isActive
                                                    ? 'text-gray-900 whitespace-nowrap'
                                                    : 'text-gray-500 whitespace-nowrap'
                                                }>{section.label}</span>
                                            </>
                                        )}
                                    </NavLink>
                                );
                            })}
                        </div>

                        {/* Business (Client/Provider) Relationship Card */}
                        <div className="md:pt-2 md:border-t md:border-gray-200">
                            <div className="rounded-xl border border-slate-200 bg-slate-50/70 p-3 space-y-2.5">
                                <div className="flex items-center gap-1.5">
                                    <IconBuildingStore size={15} className="text-blue-600 shrink-0" stroke={2} />
                                    <span className="text-xs font-bold text-slate-800 leading-tight">
                                        {t('business_profile_title')}
                                    </span>
                                </div>
                                <p className="text-[11px] leading-snug text-slate-500">
                                    {t('person_as_business_explanation')}
                                </p>

                                {linkedBusinessId ? (
                                    <div className="space-y-2">
                                        <Link
                                            target="_blank"
                                            to={`${BUSINESSES_PATH}/${linkedBusinessId}`}
                                            title={t('view_business_profile')}
                                            className="flex items-center justify-between gap-2 px-2.5 py-2 rounded-lg text-xs font-semibold text-blue-700 bg-blue-50 hover:bg-blue-100/80 transition-all border border-blue-200/80 group"
                                        >
                                            <div className="min-w-0 flex-1">
                                                <div className="line-clamp-2 break-words leading-tight">
                                                    {linkedBusinessName || t('linked_business')}
                                                </div>
                                                <div className="text-[10px] font-normal text-blue-500 mt-0.5">
                                                    {linkedBusiness?.category ? `${t(linkedBusiness.category)} · ` : ''}#{linkedBusinessId}
                                                </div>
                                            </div>
                                            <IconArrowUpRight size={14} className="text-blue-500 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform shrink-0" />
                                        </Link>

                                        <div className="flex items-center gap-1">
                                            <Button
                                                size="compact-xs"
                                                variant="subtle"
                                                color="dark"
                                                leftSection={<IconArrowsExchange size={12} />}
                                                onClick={() => setIsBusinessModalOpen(true)}
                                                loading={updatePersonMutation.isPending}
                                                className="flex-1"
                                            >
                                                {t('change_linked_pax')}
                                            </Button>
                                            <Button
                                                size="compact-xs"
                                                variant="subtle"
                                                color="red"
                                                leftSection={<IconLinkOff size={12} />}
                                                onClick={handleUnlinkBusiness}
                                                loading={updatePersonMutation.isPending}
                                            >
                                                {t('unlink')}
                                            </Button>
                                        </div>
                                    </div>
                                ) : (
                                    <Button
                                        size="compact-xs"
                                        variant="subtle"
                                        color="black"
                                        fullWidth
                                        leftSection={<IconLink size={14} />}
                                        onClick={() => setIsBusinessModalOpen(true)}
                                        loading={updatePersonMutation.isPending}
                                    >
                                        {t('link_business')}
                                    </Button>
                                )}
                            </div>
                        </div>
                    </div>
                </aside>

                <CardModal
                    size="820px"
                    withCloseButton={true}
                    opened={isBusinessModalOpen}
                    onClose={() => setIsBusinessModalOpen(false)}
                    header={<div>{t('business_profile_title')}</div>}
                >
                    <CreateBusinessComponent
                        preloadName={person?.name || ''}
                        onCreate={handleLinkBusiness}
                        onClose={() => setIsBusinessModalOpen(false)}
                    />
                </CardModal>

                {/* Right Side Content Column */}
                <main className="flex-1 min-w-0">
                    <Outlet context={person} />
                </main>
            </div>
        </div>
    );
}